/**
 * Plain helpers of the Configuration and Mail tabs (no React): names, tones, dates and the job form.
 */

export const ENV_NAME = /^[A-Z_][A-Z0-9_]{0,127}$/;
export const JOB_NAME = /^[a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?$/;

/** Why `name` cannot be a variable or secret name (as vfs-service says it), or null. */
export function nameProblem(name, reserved = { names: [], prefixes: [] }) {
  const n = String(name || '').trim();
  if (!n) { return 'Give it a name.'; }
  if (!ENV_NAME.test(n)) { return 'Capital letters, digits and _, not starting with a digit (e.g. STRIPE_API_KEY).'; }
  if ((reserved.names || []).includes(n) || (reserved.prefixes || []).some((p) => n.startsWith(p))) { return `${n} is set by the sandbox itself.`; }
  return null;
}

/** The largest file secret (vfs-service fileSecrets.js MAX_FILE_BYTES), unless the service says otherwise. */
export const FILE_SECRET_MAX_BYTES = 64 * 1024;

/** "812 B", "2.3 KB", "64 KB". */
export function formatBytes(bytes) {
  const n = Number(bytes);
  if (!Number.isFinite(n) || n < 0) { return ''; }
  if (n < 1024) { return `${n} B`; }
  const kb = n / 1024;
  return `${kb < 10 && !Number.isInteger(kb) ? kb.toFixed(1) : Math.round(kb)} KB`;
}

/** Bytes of text as UTF-8. */
export const textBytes = (text) => new TextEncoder().encode(String(text || '')).length;

/**
 * Why `path` cannot be a file secret's fixed path, or null (vfs-service checks it fully): absolute, no `..`, and not in
 * the project's folders.
 */
export function mountPathProblem(path) {
  const p = String(path || '').trim();
  if (!p) { return null; }
  if (!p.startsWith('/')) { return 'Give an absolute path, e.g. /var/secrets/google/key.json.'; }
  if (p.endsWith('/')) { return 'Give the path of the file, not of a folder.'; }
  if (p.split('/').some((seg) => seg === '..' || seg === '.')) { return 'Write the path without "..".'; }
  if (['/app', '/home', '/workspace', '/data', '/usr', '/proc', '/sys', '/dev'].some((x) => p === x || p.startsWith(`${x}/`))) { return 'Choose a path outside the project and the system folders, e.g. /var/secrets/<name>/key.json.'; }
  return null;
}

/** A File's content as base64 (FileReader: works for binary files too). */
export function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || '').replace(/^data:[^,]*,/, ''));
    reader.onerror = () => reject(reader.error || new Error('The file could not be read.'));
    reader.readAsDataURL(file);
  });
}

/** "Sun 18 Oct, 03:00" in the person's locale and zone, or ''. */
export function when(iso) {
  if (!iso) { return ''; }
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) { return ''; }
  return d.toLocaleString(undefined, { weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

/** "in 5 min", "2 h ago". */
export function relative(iso, now = Date.now()) {
  if (!iso) { return ''; }
  const diff = Date.parse(iso) - now;
  if (Number.isNaN(diff)) { return ''; }
  const abs = Math.abs(diff);
  const unit = abs < 60_000 ? 'less than a minute' : abs < 3_600_000 ? `${Math.round(abs / 60_000)} min` : abs < 86_400_000 ? `${Math.round(abs / 3_600_000)} h` : `${Math.round(abs / 86_400_000)} d`;
  return diff >= 0 ? `in ${unit}` : `${unit} ago`;
}

export const RUN_TONE = { ok: 'pass', failed: 'fail', timeout: 'fail', skipped: 'warn', running: 'prog' };
export const RUN_LABEL = { ok: 'OK', failed: 'Failed', timeout: 'Timed out', skipped: 'Skipped', running: 'Running' };
export const MAIL_TONE = { sent: 'pass', captured: 'info', refused: 'fail', failed: 'warn' };
export const MAIL_LABEL = { sent: 'Sent', captured: 'In Outbox', refused: 'Refused', failed: 'Failed' };

export const EMPTY_JOB = { name: '', schedule: '0 3 * * *', timezone: '', kind: 'command', command: '', path: '', method: 'POST', timeoutSeconds: 300, enabled: true };

/** The form's state from a saved job. */
export function jobToForm(job) {
  if (!job) { return { ...EMPTY_JOB, timezone: browserZone() }; }
  return {
    name: job.name,
    schedule: job.schedule,
    timezone: job.timezone || 'UTC',
    kind: job.path ? 'http' : 'command',
    command: job.command || '',
    path: job.path || '',
    method: job.method || 'POST',
    timeoutSeconds: job.timeoutSeconds || 300,
    enabled: job.enabled !== false,
    description: job.description || '',
  };
}

/** The job to save from the form (what vfs-service validates). */
export function formToJob(form) {
  const job = {
    name: String(form.name || '').trim(),
    schedule: String(form.schedule || '').trim(),
    timezone: String(form.timezone || '').trim() || 'UTC',
    timeoutSeconds: Number(form.timeoutSeconds) || 300,
    enabled: form.enabled !== false,
  };
  if (form.kind === 'http') { Object.assign(job, { path: String(form.path || '').trim(), method: form.method || 'POST' }); } else { job.command = String(form.command || '').trim(); }
  if (form.description) { job.description = String(form.description).trim(); }
  return job;
}

/** What is missing in the form before it can be sent, or null. */
export function jobProblem(form) {
  if (!JOB_NAME.test(String(form.name || '').trim())) { return 'Name: lower-case letters, digits and - (e.g. nightly-report).'; }
  if (String(form.schedule || '').trim().split(/\s+/).length !== 5 && !String(form.schedule || '').trim().startsWith('@')) { return 'Schedule: 5 fields (minute hour day month weekday), e.g. 0 3 * * *.'; }
  if (form.kind === 'http' && !String(form.path || '').startsWith('/')) { return 'Path: starts with /, e.g. /internal/cleanup.'; }
  if (form.kind !== 'http' && !String(form.command || '').trim()) { return 'Command: what to run, e.g. node scripts/report.js.'; }
  return null;
}

export function browserZone() {
  try { return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC'; } catch { return 'UTC'; }
}

export const SCHEDULE_EXAMPLES = [
  { value: '*/15 * * * *', label: 'every 15 minutes' },
  { value: '0 * * * *', label: 'every hour' },
  { value: '0 3 * * *', label: 'every day at 03:00' },
  { value: '0 9 * * 1-5', label: 'weekdays at 09:00' },
  { value: '0 6 1 * *', label: 'the 1st of every month' },
];

export function errorText(error) {
  if (!error) { return ''; }
  if (error.status === 0) { return 'Configuration is not available here.'; }
  const errors = error.body?.errors;
  return Array.isArray(errors) && errors.length ? `${error.message}: ${errors.join('; ')}` : (error.message || 'Something went wrong.');
}
